import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from "react";
import QrScannerView from "../../components/QrScannerView/QrScannerView";
import {
  decodeQrData,
  scanFilesForQr,
  scanFilesWithURLForQr,
} from "../../utils/qr-utils";
import * as pdfjsLib from "pdfjs-dist/webpack";
import ScannerBorderIcon from "../../resources/icons/scanner-ouline.svg";
import ScanIcon from "../../resources/icons/scan-svgrepo-com.svg";
import ScanIconHover from "../../resources/icons/scan-svgrepo-com-hover.svg";
import UploadIcon from "../../resources/icons/upload-minimalistic-svgrepo-com.svg";
import UploadIconHover from "../../resources/icons/upload-minimalistic-svgrepo-com-hover.svg";
import "./Validate.css";
import toast from "react-hot-toast";
import { verify } from "../../utils/verification-utils";
import { resolveEncodedDataFromVc } from "../../utils/vc-context";
import { CLAIM169_QR_PREFIX } from "../../utils/config";
import useInterceptedNavigate from "../../hooks/useInterceptedNavigate";
import PaymentFailedModal from "../../common/PaymentFailedModal/PaymentFailedModal";
import ErrorIcon from "../../resources/icons/error-live-icon.svg";
import WarningIcon from "../../resources/icons/warning-live-icon.svg";
import { useLocation } from "react-router-dom";
import { POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import UploadConfirmationModal from "../../common/UploadConfirmationModal/UploadConfirmationModal";
import CloseIcon from "../../resources/icons/x-close-white.svg";
import CfLoader from "../../common/CfLoader/CfLoader";
import ImageCropper from "../../components/ImageCropper/ImageCropper";
import QRCodeConfirmationModal from "../../components/QRCodeConfirmationModal/QRCodeConfirmationModal";
import { useTranslation } from "react-i18next";
import AuthContext from "../../store/auth-context";
import { useTheme } from "../../store/ThemeContext";
import ServiceUnavailableModal from "../../common/ServiceUnavailableModal/ServiceUnavailableModal";
import {
  isOcrFlowEnabled,
  isSunbirdValidationEnabled,
} from "../../configs/featureFlags";
import { isCredentialSubjectUnavailable } from "../../utils/vcVerificationDisplay";
import { getCameraErrorMessage } from "../../utils/cameraErrors";

const Validate = ({ setCurrentStep }) => {
  const navigate = useInterceptedNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);

  const { state } = location;
  const showPaymentFailedModal = state?.showPaymentFailedModal || false;
  const showPaymentPendingModal = state?.showPaymentPendingModal || false;

  const [activeTab, setActiveTab] = useState("upload");
  const [isScanning, setIsScanning] = useState(false);
  const [checks, setChecks] = useState([]);
  const [currentScanIcon, setCurrentScanIcon] = useState(ScanIcon);
  const [currentUploadIcon, setCurrentUploadIcon] = useState(UploadIcon);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const [credentialId, setCredentialId] = useState("");
  const [credentialInput, setCredentialInput] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [showUploadConfirmationModal, setShowUploadConfirmationModal] =
    useState(false);
  const [uploadedImageSrc, setUploadedImageSrc] = useState("");
  const [uploadedFileType, setUploadedFileType] = useState("");
  const [uploadedImage, setUploadedImage] = useState(null);
  const [decodedData, setDecodedData] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState("");
  const [retryAttempted, setRetryAttempted] = useState(false);
  const [apiCallFailed, setApiCallFailed] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrModalLoading, setQrModalLoading] = useState(false);
  const [pendingOcrData, setPendingOcrData] = useState({
    uploadedImage: null,
    uploadedFileType: null,
    uploadedImageSrc: null,
  });
  const [isInitialSetupDone, setIsInitialSetupDone] = useState(false);
  const [showServiceUnavailableModal, setShowServiceUnavailableModal] =
    useState(false);

  const selectedLanguage = authContext.selectedLanguage;
  const { verificationMethods } = useTheme();
  const allowedWithQr =
    (verificationMethods && verificationMethods.with_qr) || [];
  const withQrLower = allowedWithQr.map((s) =>
    (s || "").toString().toLowerCase(),
  );
  const hasScan = withQrLower.includes("scan");
  const hasUpload = withQrLower.includes("upload");
  const hasCredentialId = withQrLower.includes("credentialid");

  const fileInputRef = useRef(null);
  const scanProcessingRef = useRef(false);
  const handleScanRef = useRef(null);
  const handleScannerErrorRef = useRef(null);

  useEffect(() => {
    if (
      uploadedImage &&
      decodedData &&
      showUploadConfirmationModal &&
      !apiCallFailed
    ) {
      verifyCredential(decodedData);
    }
  }, [uploadedImage, decodedData, showUploadConfirmationModal, apiCallFailed]);

  useEffect(() => {
    if (!verificationMethods) return;

    if (activeTab === "scanner" && !hasScan) {
      if (hasUpload) setActiveTab("upload");
      else if (hasCredentialId) setActiveTab("credentialId");
      else setActiveTab("");
    } else if (activeTab === "upload" && !hasUpload) {
      if (hasScan) setActiveTab("scanner");
      else if (hasCredentialId) setActiveTab("credentialId");
      else setActiveTab("");
    } else if (activeTab === "credentialId" && !hasCredentialId) {
      if (hasScan) setActiveTab("scanner");
      else if (hasUpload) setActiveTab("upload");
      else setActiveTab("");
    } else if (!activeTab) {
      if (hasUpload) setActiveTab("upload");
      else if (hasScan) setActiveTab("scanner");
      else if (hasCredentialId) setActiveTab("credentialId");
    }

    setIsInitialSetupDone(true);
  }, [verificationMethods, hasScan, hasUpload, hasCredentialId]);

  const enableSubmitButton = (event) => {
    let currentId = event.target.value.replace(/[^a-zA-Z0-9]/g, "");

    if (currentId.length > 12) {
      currentId = currentId.substring(0, 12);
    }

    setCredentialInput(currentId);

    if (currentId.length === 12) {
      setIsSubmitEnabled(true);
      setCredentialId("credissuer:" + currentId);
    } else {
      setIsSubmitEnabled(false);
      setCredentialId("");
    }
  };

  const submitCredential = (event) => {
    event.preventDefault();
    setIsValidating(true);
    sessionStorage.setItem("file_Uploaded", "false");
    verifyCredential(credentialId);
  };

  const verifyCredential = async (credential) => {
    setIsLoading(true);
    setApiCallFailed(false);
    setIsValidating(true);

    let credentialId;
    if (
      typeof credential === "string" &&
      credential.startsWith(CLAIM169_QR_PREFIX)
    ) {
      credentialId = credential;
    } else {
      try {
        const credentialJson = JSON.parse(credential);
        credentialId =
          (await resolveEncodedDataFromVc(credentialJson)) ||
          credentialJson?.id;
      } catch (err) {
        console.warn("Invalid JSON string passed as credential:", credential);
        credentialId = credential;
      }
    }

    const body =
      sessionStorage.getItem("file_Uploaded") === "true"
        ? { document: uploadedImage, encoded_data: credentialId }
        : { encoded_data: credentialId };

    try {
      const res = await POST(EntityConstants.VERIFY_CREDENTIAL, body);
      const data = await res.json();
      const ResData = { res, data };

      setResponseData(ResData);
      if (ResData.res.ok) {
        setIsValidating(false);
        setApiCallFailed(false);

        if (isCredentialSubjectUnavailable(ResData.data)) {
          setShowUploadConfirmationModal(false);
          if (isScanning) {
            closeScanner();
          }
          setShowServiceUnavailableModal(true);
          return;
        }

        if (sessionStorage.getItem("file_Uploaded") === "true") {
          setShowUploadConfirmationModal(true);
        } else {
          if (isScanning) {
            closeScanner();
          }
          handleNavigation(ResData);
        }
      } else if (ResData.res.status === 400) {
        toast.error(ResData.data.message);
        setIsValidating(false);
        setApiCallFailed(true);
        if (isScanning) {
          closeScanner();
        }
      } else {
        toast.error(data.message || t("verify:verification_failed"));
        setIsValidating(false);
        setApiCallFailed(true);
        if (isScanning) {
          closeScanner();
        }
      }
    } catch (err) {
      setIsValidating(false);
      setApiCallFailed(true);
      if (isScanning) {
        closeScanner();
      }

      if (err.response) {
        err.response
          .json()
          .then((errorData) => {
            if (errorData.message) {
              toast.error(errorData.message);
            }
          })
          .catch((jsonErr) =>
            console.log("Error parsing JSON response", jsonErr),
          );
      } else {
        toast.error(t("verify:unexpected_error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const input = e.target;
    resetValidationState();
    setChecks([]);
    setRetryAttempted(false);

    if (input.files.length > 0) {
      const file = input.files[0];

      try {
        let scanResult = await scanFilesWithURLForQr(file);

        if (!scanResult?.data) {
          scanResult = await scanFilesForQr(file);
        }

        if (!scanResult?.data) {
          const fullPageBlob = await convertToFullPageImage(file);

          const fullPageFile = new File([fullPageBlob], "full-page.jpg", {
            type: "image/jpeg",
          });
          scanResult = await scanFilesForQr(fullPageFile);

          if (!scanResult?.data) {
            const reader = new FileReader();
            reader.onload = (event) => {
              setCropperImageSrc(event.target.result);
              setShowCropper(true);
            };

            if (file.type === "application/pdf") {
              reader.readAsDataURL(file);
            } else {
              reader.readAsDataURL(fullPageBlob);
            }
            return;
          }
        }

        let decodedTempData = await decodeQrData(scanResult);
        if (
          decodedTempData &&
          typeof decodedTempData === "string" &&
          decodedTempData.startsWith("https:")
        ) {
          decodedTempData = extractEncodedData(decodedTempData);
        }

        setIsValidating(false);
        if (decodedTempData) {
          if (typeof decodedTempData === "string") {
            sessionStorage.setItem("file_Uploaded", "true");
            setDecodedData(decodedTempData);
          } else {
            decodeVcData(decodedTempData);
          }
        }
      } catch (error) {
        console.error("Error scanning file:", error);
        toast.error(t("verify:scan_failed_retry"));
        setIsValidating(false);
      } finally {
        input.value = "";
      }
    }
  };

  const convertToFullPageImage = async (file) => {
    try {
      if (file.type === "application/pdf") {
        return await convertPdfFirstPageToImage(file);
      } else {
        return new Blob([file], { type: file.type });
      }
    } catch (error) {
      console.error("Error converting to full page image:", error);
      throw error;
    }
  };

  const convertPdfFirstPageToImage = async (pdfFile, dpi = 300) => {
    const pdfDataU8 = await readFileAsUint8Array(pdfFile);
    const pdf = await pdfjsLib.getDocument({ data: pdfDataU8 }).promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: dpi / 72 });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
    return new Promise((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.95);
    });
  };

  const readFileAsUint8Array = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(new Uint8Array(reader.result));
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleCroppedImage = async (croppedBlob) => {
    setShowCropper(false);
    setIsValidating(true);

    try {
      const croppedFile = new File([croppedBlob], "cropped-qr-area.jpg", {
        type: "image/jpeg",
      });

      let scanResult = await scanFilesWithURLForQr(croppedFile);
      if (!scanResult?.data) {
        scanResult = await scanFilesForQr(croppedFile);
      }

      if (!scanResult?.data) {
        setRetryAttempted(true);

        if (showUploadConfirmationModal) {
          setShowUploadConfirmationModal(false);
        }

        setIsValidating(false);

        if (isOcrFlowEnabled()) {
          setPendingOcrData({
            uploadedImage: uploadedImage,
            uploadedFileType: uploadedFileType,
            uploadedImageSrc: uploadedImageSrc,
          });
          setShowQRModal(true);
        } else {
          toast.error(t("verify:unable_to_scan_selected_area"));
          setShowCropper(true);
        }
        return;
      }

      let decodedTempData = await decodeQrData(scanResult);
      if (
        decodedTempData &&
        typeof decodedTempData === "string" &&
        decodedTempData.startsWith("https:")
      ) {
        decodedTempData = extractEncodedData(decodedTempData);
      }

      if (decodedTempData) {
        if (typeof decodedTempData === "string") {
          sessionStorage.setItem("file_Uploaded", "true");
          setDecodedData(decodedTempData);
        } else {
          decodeVcData(decodedTempData);
        }
      }

      setIsValidating(false);
    } catch (error) {
      console.error("Error processing cropped image:", error);

      if (showUploadConfirmationModal) {
        setShowUploadConfirmationModal(false);
      }

      setIsValidating(false);

      if (isOcrFlowEnabled()) {
        setPendingOcrData({
          uploadedImage: uploadedImage,
          uploadedFileType: uploadedFileType,
          uploadedImageSrc: uploadedImageSrc,
        });
        setShowQRModal(true);
      } else {
        toast.error(t("verify:unable_to_scan_selected_area"));
        setShowCropper(true);
      }
    }
  };

  const handleQRModalConfirm = async () => {
    if (!isOcrFlowEnabled()) {
      return;
    }

    setQrModalLoading(true);

    try {
      if (!pendingOcrData.uploadedImage || !pendingOcrData.uploadedFileType) {
        throw new Error(t("verify:no_ocr_file"));
      }

      navigate("/verifier/ocr/processing", {
        state: {
          uploadedFile: pendingOcrData.uploadedImage,
          uploadedFileType: pendingOcrData.uploadedFileType,
          uploadedImageSrc: pendingOcrData.uploadedImageSrc,
          flowType: "ocr",
        },
      });
    } catch (error) {
      console.error("OCR initiation error:", error);
      toast.error(t("verify:ocr_start_failed"));
    } finally {
      setQrModalLoading(false);
      setShowQRModal(false);
    }
  };

  const handleQRModalCancel = () => {
    setShowQRModal(false);
    setIsValidating(false);
    resetValidationState();

    setPendingOcrData({
      uploadedImage: null,
      uploadedFileType: null,
      uploadedImageSrc: null,
    });
  };

  const handleScan = async (data) => {
    if (!data?.[0]?.rawValue || scanProcessingRef.current) return;

    scanProcessingRef.current = true;
    resetValidationState();
    setIsValidating(true);
    setIsLoading(true);

    try {
      const rawValue = data[0].rawValue;

      sessionStorage.setItem("file_Uploaded", "false");
      setUploadedImage(null);

      if (rawValue.startsWith(CLAIM169_QR_PREFIX)) {
        await verifyCredential(rawValue);
        return;
      }

      const decodedTempData = extractEncodedData(rawValue);

      if (decodedTempData) {
        await verifyCredential(decodedTempData);
      } else {
        await decodeVcData(rawValue);
      }
    } catch (err) {
      toast.error(t("verify:invalid_certificate_data"));
      setIsValidating(false);
      scanProcessingRef.current = false;
    }
  };

  handleScanRef.current = handleScan;

  const stableHandleScan = useCallback((data) => {
    handleScanRef.current?.(data);
  }, []);

  const handleScannerError = (error) => {
    console.error("Scanner error:", error);
    setIsLoading(false);
    setIsValidating(false);
    toast.error(getCameraErrorMessage(error, t));
    closeScanner();
  };

  handleScannerErrorRef.current = handleScannerError;

  const stableHandleScannerError = useCallback((error) => {
    handleScannerErrorRef.current?.(error);
  }, []);

  useEffect(() => {
    return () => {
      window.__cameraPermissionPending = false;
    };
  }, []);

  const closeScanner = () => {
    window.__cameraPermissionPending = false;
    scanProcessingRef.current = false;
    setIsScanning(false);
    setIsValidating(false);
    setActiveTab("upload");
  };

  const resetValidationState = () => {
    setApiCallFailed(false);
    setIsValidating(false);
    setDecodedData(null);
    setResponseData(null);
    setChecks([]);
    setRetryAttempted(false);
  };

  const handleNavigation = (ResData) => {
    const data = ResData.data || {};
    const applicationStatus = data.application_status || data.status;
    const verificationCost = data.verification_cost || {};

    if (isCredentialSubjectUnavailable(data)) {
      setShowServiceUnavailableModal(true);
      return;
    }

    if (data.is_payment_required) {
      navigate(
        `/verifier/vc/payment-details/${data.verification_log_id}/${data.order_id}`,
        {
          state: {
            issuerName: data.issuer_name,
            programmeName: data.programme_name,
            razorpayClientId: data.razorpay_client_id,
            verificationTotalAmount: data.total_verification_cost,
            verificationAmount: verificationCost.verification_fee,
            platformFee: verificationCost.platform_fee,
            igst: verificationCost.igst_fee,
            cgst: verificationCost.cgst_fee,
            sgst: verificationCost.sgst_fee,
            gst: verificationCost.gst_fee,
            uploadedDocument: data.uploaded_document,
            underReviewStatus: applicationStatus,
            application_status: applicationStatus,
            application_status_message: data.application_status_message,
            vc_status: data.vc_status,
            underReviewProgrammeName: data.programme_name,
            underReviewIssuerName: data.issuer_name,
            underReviewCandidateName: data.recipient_name,
            underReviewCandidateEmail: data.recipient_email,
            certificateId: data.certificate_id,
            igstPercentage: verificationCost.igst_percentage,
            cgstPercentage: verificationCost.cgst_percentage,
            sgstPercentage: verificationCost.sgst_percentage,
            gstPercentage: verificationCost.gst_percentage,
            document_type: uploadedFileType,
            orderId: data.order_id,
            verificationLogId: data.verification_log_id,
            issuerOrganisation: data.issuer_organisation,
            paymentRequired: data.is_payment_required,
            credential_subject: data.credential_subject,
            currentStep: 1,
            flowType: "vc",
          },
        },
      );
    } else if (
      data.is_payment_required === false &&
      applicationStatus === "Approval Pending"
    ) {
      navigate(`/verifier/vc/summary`, {
        state: {
          issuerName: data.issuer_name,
          programmeName: data.programme_name,
          verificationTotalAmount: data.total_verification_cost,
          verificationAmount: verificationCost.verification_fee,
          platformFee: verificationCost.platform_fee,
          igst: verificationCost.igst_fee,
          cgst: verificationCost.cgst_fee,
          sgst: verificationCost.sgst_fee,
          gst: verificationCost.gst_fee,
          uploadedDocument: data.uploaded_document,
          underReviewStatus: applicationStatus,
          application_status: applicationStatus,
          application_status_message: data.application_status_message,
          vc_status: data.vc_status,
          status: applicationStatus,
          underReviewProgrammeName: data.programme_name,
          underReviewIssuerName: data.issuer_name,
          underReviewCandidateName: data.recipient_name,
          underReviewCandidateEmail: data.recipient_email,
          certificateId: data.certificate_id,
          verificationCost: verificationCost,
          reviewcomments: data.comments,
          underReviewSignature: data.signature,
          underReviewPaymentId: data.payment_id,
          igstPercentage: verificationCost.igst_percentage,
          cgstPercentage: verificationCost.cgst_percentage,
          sgstPercentage: verificationCost.sgst_percentage,
          gstPercentage: verificationCost.gst_percentage,
          document_type: uploadedFileType,
          orderId: data.order_id,
          verificationLogId: data.verification_log_id,
          issuerOrganisation: data.issuer_organisation,
          paymentRequired: data.is_payment_required,
          credential_subject: data.credential_subject,
          currentStep: 2,
          flowType: "vc",
        },
      });
    } else if (
      data.is_payment_required === false &&
      ["Approved", "Rejected", "Revoked"].includes(applicationStatus)
    ) {
      navigate(`/verifier/vc/summary`, {
        state: {
          issuerName: data.issuer_name,
          programmeName: data.programme_name,
          verificationTotalAmount: data.total_verification_cost,
          verificationAmount: verificationCost.verification_fee,
          platformFee: verificationCost.platform_fee,
          igst: verificationCost.igst_fee,
          cgst: verificationCost.cgst_fee,
          sgst: verificationCost.sgst_fee,
          gst: verificationCost.gst_fee,
          uploadedDocument: data.uploaded_document,
          transaction_verification_status: applicationStatus,
          programme_name: data.programme_name,
          issuer: data.issuer_name,
          recipient_name: data.recipient_name,
          recipient_email: data.recipient_email,
          transaction_certificate_id: data.certificate_id,
          payment_id: data.payment_id,
          signature: data.signature,
          igstPercentage: verificationCost.igst_percentage,
          cgstPercentage: verificationCost.cgst_percentage,
          sgstPercentage: verificationCost.sgst_percentage,
          gstPercentage: verificationCost.gst_percentage,
          document_type: uploadedFileType,
          pdf_url: data.verification_details?.pdf_url,
          orderId: data.order_id,
          verificationLogId: data.verification_log_id,
          issuerOrganisation: data.issuer_organisation,
          status: applicationStatus,
          application_status: applicationStatus,
          application_status_message: data.application_status_message,
          vc_status: data.vc_status,
          paymentStatus: data.payment_status || "Not Required",
          credential_subject: data.credential_subject,
          currentStep: 2,
          flowType: "vc",
        },
      });
    }
  };

  const extractEncodedData = (data) => {
    setApiCallFailed(false);
    try {
      if (typeof data !== "string") {
        console.error(
          "extractEncodedData: Expected a string, got",
          typeof data,
        );
        return null;
      }
      const urlParamsRegex = /[?&]encoded_data=([^&]+)/;
      const urlPathRegex = /\/verify\/([a-f0-9]{64})$/;
      let match = data.match(urlPathRegex);
      if (match) {
        setApiCallFailed(false);
        return match[1];
      }
      match = data.match(urlParamsRegex);
      if (match) {
        setApiCallFailed(false);
        return match[1];
      }
      return null;
    } catch (error) {
      setApiCallFailed(true);
      toast.error(t("verify:invalid_qr_issuer"));
      return null;
    }
  };

  const decodeVcData = async (data) => {
    try {
      const decodedData = await decodeQrData(data);
      const decodedDataJSON = JSON.parse(decodedData);

      if (isSunbirdValidationEnabled()) {
        const { status, checks: verifyChecks } = await verify(decodedDataJSON);
        setChecks(verifyChecks);
        if (status === "OK") {
          const encodedData = await resolveEncodedDataFromVc(decodedDataJSON);
          verifyCredential(encodedData || decodedDataJSON.id);
          return;
        }
      }

      const encodedData = await resolveEncodedDataFromVc(decodedDataJSON);
      if (encodedData) {
        verifyCredential(encodedData);
      } else {
        toast.error(t("verify:invalid_qr_issuer"));
        setIsLoading(false);
        setIsValidating(false);
        scanProcessingRef.current = false;
        if (isScanning) {
          closeScanner();
        }
      }
    } catch (error) {
      console.error("Failed to decode VC data:", error);
      toast.error(t("verify:invalid_qr_issuer"));
      setIsLoading(false);
      setIsValidating(false);
      scanProcessingRef.current = false;
      if (isScanning) {
        closeScanner();
      }
    }
  };

  const validateFile = (e) => {
    resetValidationState();
    setIsValidating(true);
    const input = e.target;
    const file = input.files[0];

    setUploadedImage(null);
    setUploadedImageSrc("");
    setDecodedData(null);
    setResponseData(null);
    sessionStorage.setItem("file_Uploaded", "false");

    const allowedFormats = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "application/pdf",
    ];
    const minSize = 10 * 1024;
    const maxSize = 20 * 1024 * 1024;

    if (!allowedFormats.includes(file.type)) {
      toast.error(t("verify:invalid_file_format"));
      setIsValidating(false);
      return;
    }

    if (file.size < minSize || file.size > maxSize) {
      setIsValidating(false);
      toast.error(t("verify:invalid_file_size"));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64Document = reader.result;
      setUploadedImageSrc(base64Document);
      setUploadedImage(base64Document);
      setUploadedFileType(file.type);
      sessionStorage.setItem("file_Uploaded", "true");
      setShowUploadConfirmationModal(true);
      setIsOpen(true);

      handleFileChange(e);
    };
    reader.readAsDataURL(file);
  };

  const handleNext = () => {
    if (!responseData) return;
    setShowUploadConfirmationModal(false);
    setCurrentStep(1);
    handleNavigation(responseData);
  };

  const handleUploadAgain = () => {
    resetValidationState();
    setShowUploadConfirmationModal(false);
    setUploadedImage(null);
    setUploadedImageSrc("");
    setDecodedData(null);
    setResponseData(null);
    sessionStorage.setItem("file_Uploaded", "false");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);
  };

  const closeModal = () => {
    setIsOpen(false);
    if (showUploadConfirmationModal) {
      resetValidationState();
      setShowUploadConfirmationModal(false);
      setUploadedImage(null);
      setUploadedImageSrc("");
      setDecodedData(null);
      setResponseData(null);
      sessionStorage.setItem("file_Uploaded", "false");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const renderScannerContent = () => {
    if (activeTab === "scanner" && isScanning) {
      return (
        <>
          <button
            id="verify-scanner-close-button"
            onClick={closeScanner}
            className="absolute top-2 right-2 transform focus:outline-none bg-red-500 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center shadow-lg hover:bg-red-600 transition-all duration-500"
            style={{ zIndex: 9 }}
          >
            <img
              src={CloseIcon}
              className="h-4 w-4 sm:h-5 sm:w-5"
              alt="Close"
            />
          </button>
          <div className="absolute inset-0 overflow-hidden rounded-lg">
            <QrScannerView
              onScan={stableHandleScan}
              onError={stableHandleScannerError}
            />
          </div>
        </>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col items-center w-full bg-cfSecondary min-h-screen overflow-x-hidden">
      <ServiceUnavailableModal
        isOpen={showServiceUnavailableModal}
        onClose={() => setShowServiceUnavailableModal(false)}
      />
      <QRCodeConfirmationModal
        isOpen={showQRModal}
        onConfirm={handleQRModalConfirm}
        onCancel={handleQRModalCancel}
        isLoading={qrModalLoading}
      />

      {showPaymentFailedModal && (
        <PaymentFailedModal
          modalHeading={t("verify:payment_failed")}
          modalMessage={t("verify:payment_failed_desc")}
          isOpen={isOpen}
          closeModal={closeModal}
          modalIcon={ErrorIcon}
          paymentId={localStorage.getItem("payment_id")}
        />
      )}
      {showPaymentPendingModal && (
        <PaymentFailedModal
          modalHeading={t("verify:payment_pending")}
          modalMessage={t("verify:payment_pending_desc")}
          isOpen={isOpen}
          closeModal={closeModal}
          modalIcon={WarningIcon}
          paymentId={localStorage.getItem("payment_id")}
        />
      )}
      {!showCropper && showUploadConfirmationModal && (
        <UploadConfirmationModal
          imageSrc={uploadedImageSrc}
          uploadedFileType={uploadedFileType}
          onUploadAgain={handleUploadAgain}
          onNext={handleNext}
          isOpen={isOpen}
          closeModal={closeModal}
          isValidating={isValidating}
          apiCallFailed={apiCallFailed}
          decodedData={decodedData}
        />
      )}
      {showCropper && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <ImageCropper
            imageSrc={cropperImageSrc}
            onCrop={handleCroppedImage}
            onCancel={() => {
              setShowCropper(false);
              setIsValidating(false);
              if (!retryAttempted) {
                toast(t("verify:try_upload_again"));
              }
            }}
          />
        </div>
      )}
      <div className="w-full flex flex-col">
        <div className="w-full flex flex-col items-center sm:items-start">
          <p className="sm:hidden px-4">
            {t("verify:scan_upload_instruction")}
          </p>
        </div>
      </div>
      {!isLoading ? (
        <div className="w-full flex flex-col xl:flex-row items-center justify-center xl:justify-between">
          <div className="w-full xl:w-full flex justify-center">
            <div className="mx-4 mb-9 w-[90vw] h-[90vw] sm:min-w-[26rem] sm:min-h-[26rem] sm:w-[26rem] sm:h-[26rem] relative max-w-md">
              <img src={ScannerBorderIcon} alt="" className="w-full absolute" />
              <div className="w-full h-full p-4 flex flex-col justify-center items-center absolute top-0 left-0">
                <div
                  className={`relative flex flex-col justify-between w-full h-full rounded-lg bg-[#3538cd18] ${
                    isScanning ? "p-0" : "p-3 sm:p-5"
                  }`}
                >
                  {renderScannerContent()}

                  {!isScanning && (
                    <div className="w-full sm:w-[14rem] gap-2 sm:h-96 flex flex-col justify-center text-cfPrimary text-xl sm:text-2xl font-semibold mx-auto my-auto">
                      {hasScan && (
                        <button
                          id="verify-scan-button"
                          className="cursor-pointer px-8 sm:px-14 py-2 sm:py-[0.8rem] bg-sfSecondary border-2 border-cfPrimary hover:text-cfSecondary hover:bg-cfButton flex items-center justify-center gap-2 rounded-full"
                          onClick={() => {
                            window.__cameraPermissionPending = true;
                            setActiveTab("scanner");
                            setIsScanning(true);
                            setUploadedImage(null);
                            sessionStorage.setItem("file_Uploaded", "false");
                          }}
                          onMouseOver={() => setCurrentScanIcon(ScanIconHover)}
                          onMouseOut={() => setCurrentScanIcon(ScanIcon)}
                        >
                          <img
                            src={currentScanIcon}
                            alt=""
                            className="w-6 h-6 sm:w-10 sm:h-10"
                          />
                          <p>{t("verify:scan")}</p>
                        </button>
                      )}
                      {activeTab === "upload" && hasUpload && (
                        <label
                          id="verify-upload-button"
                          htmlFor="qr-upload"
                          className="cursor-pointer px-8 sm:px-14 py-2 sm:py-[0.8rem] bg-sfSecondary border-2 border-cfPrimary hover:text-cfSecondary hover:bg-cfButton w-full self-center text-center flex items-center justify-center gap-1 rounded-full m-0"
                          onMouseOver={() =>
                            setCurrentUploadIcon(UploadIconHover)
                          }
                          onMouseOut={() => setCurrentUploadIcon(UploadIcon)}
                        >
                          <img
                            src={currentUploadIcon}
                            alt=""
                            className="w-6 h-6 sm:w-10 sm:h-10"
                          />
                          <p>{t("verify:upload")}</p>
                        </label>
                      )}
                      {hasCredentialId && (
                        <form className="flex flex-col gap-4 sm:gap-7">
                          <div className="self-center border-2 border-cfPrimary rounded-full overflow-clip px-1 py-2 sm:py-[0.8rem] bg-cfSecondary w-full flex justify-center items-center focus:outline-none">
                            <input
                              id="verify-credential-id-input"
                              type="text"
                              name="credential-id"
                              value={credentialInput}
                              placeholder={t("verify:credential_id")}
                              className="bg-sfSecondary border-none focus:outline-none self-center text-center text-sm sm:text-base w-full"
                              onChange={(event) => enableSubmitButton(event)}
                            />
                          </div>
                          <button
                            id="verify-credential-id-submit-button"
                            type="submit"
                            disabled={!isSubmitEnabled}
                            onClick={(event) => submitCredential(event)}
                            className="w-full self-center px-4 py-2 rounded-lg bg-cfButton hover:bg-cfPrimaryDark disabled:bg-gray-300 text-sm sm:text-base text-white disabled:cursor-not-allowed"
                          >
                            {t("verify:submit")}
                          </button>
                        </form>
                      )}
                    </div>
                  )}
                  {activeTab === "upload" && hasUpload && (
                    <input
                      type="file"
                      id="qr-upload"
                      className="hidden"
                      onChange={validateFile}
                      ref={fileInputRef}
                    />
                  )}
                </div>
              </div>
              {!isScanning && (
                <div
                  className={`absolute -bottom-28 ${
                    selectedLanguage === "en"
                      ? "sm:-bottom-24"
                      : "sm:-bottom-36"
                  } transform -translate-x-1/2 left-1/2 w-full text-sm sm:text-base lg:text-2xl font-medium text-black`}
                >
                  <p className="text-center">
                    {t("verify:allowed_formats_info")}
                  </p>
                  <p className="text-center">{t("verify:file_size_limits")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-[2rem]">
          <CfLoader />
        </div>
      )}
    </div>
  );
};

export default Validate;
